import { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import type { UsagePiece } from '@/services/usagePiecesService';

/**
 * Share of generated pieces by LLM model — a donut (parts of a whole). Categorical
 * hues assigned in a FIXED order (dataviz: never cycled); models past the palette
 * fold into a muted "Other" rather than inventing colours.
 */
const PALETTE = [
  'var(--color-coral)',
  'var(--color-teal-dark)',
  'var(--color-violet)',
  'var(--color-amber)',
];
const OTHER_COLOR = 'var(--color-muted-foreground)';

function prettyModel(model: string | null): string {
  if (!model) return 'Unknown';
  // "google/gemini-3-pro-preview" -> "gemini-3-pro-preview"
  return model.includes('/') ? model.slice(model.lastIndexOf('/') + 1) : model;
}

export function UsageModelsDonut({ pieces }: { pieces: UsagePiece[] }) {
  const slices = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of pieces) {
      const key = prettyModel(p.model);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    const named = sorted.slice(0, PALETTE.length).map(([name, value], i) => ({
      name,
      value,
      color: PALETTE[i],
    }));
    const rest = sorted.slice(PALETTE.length).reduce((sum, [, v]) => sum + v, 0);
    return rest > 0 ? [...named, { name: 'Other', value: rest, color: OTHER_COLOR }] : named;
  }, [pieces]);

  const total = slices.reduce((s, x) => s + x.value, 0);

  if (total === 0) {
    return (
      <div className="flex h-56 items-center justify-center text-sm text-muted-foreground">
        No pieces to break down yet.
      </div>
    );
  }

  // Custom legend beside the donut: recharts' built-in legend overlaps the pie
  // once model names get long. A flex row keeps the donut fixed and lets names
  // truncate.
  return (
    <div className="flex items-center gap-4">
      <div className="h-48 w-40 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={44}
              outerRadius={72}
              paddingAngle={2}
              stroke="var(--color-warm-bg)"
              strokeWidth={2}
            >
              {slices.map((s) => (
                <Cell key={s.name} fill={s.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 12 }}
              formatter={(value: number, name) => [
                `${value} piece${value === 1 ? '' : 's'} (${Math.round((value / total) * 100)}%)`,
                name,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="min-w-0 flex-1 space-y-1.5 text-sm">
        {slices.map((s) => (
          <li key={s.name} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: s.color }}
            />
            <span className="truncate text-foreground" title={s.name}>
              {s.name}
            </span>
            <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
              {s.value} · {Math.round((s.value / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
